import React, { createContext, useState, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { CORE_STAFF_ROLES } from '../config/roleConfig';

/**
 * AccountContext - Manages selected account for admin/ops users
 *
 * This context allows admin and operations staff to switch between
 * different user accounts to view their properties and bookings.
 *
 * Pattern: Follows Salesforce/Stripe account switching model
 *
 * Security: Role-validated init — only core staff roles can hold a
 * selectedAccount value. Stale sessionStorage values from previous
 * admin sessions are ignored and cleaned up for non-staff users.
 */

const AccountContext = createContext({
  selectedAccount: null,
  setSelectedAccount: () => {},
  clearAccount: () => {}
});

const STORAGE_KEY = 'hostme_selected_account';

export const AccountProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const userRole = user?.role;
  const isCoreStaff = CORE_STAFF_ROLES.includes(userRole);

  // Initialize from sessionStorage — only if current user is core staff
  // Note: reads role from localStorage directly (not AuthContext) because
  // AuthContext.user is null on first render (set in useEffect, not useState).
  // This mirrors AuthContext's own init pattern (lines 37-44).
  const [selectedAccount, setSelectedAccountState] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const storedRole = localStorage.getItem('userRole');
      if (!CORE_STAFF_ROLES.includes(storedRole)) {
        // Stale value from a previous staff session — clean it up
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return stored;
    } catch (error) {
      // SessionStorage not available (e.g., incognito mode)
      console.warn('SessionStorage not available:', error);
      return null;
    }
  });

  // Persist to sessionStorage on change — guarded by role
  const setSelectedAccount = (accountId) => {
    if (accountId && !isCoreStaff) {
      // Non-staff users cannot set an account selection
      return;
    }

    setSelectedAccountState(accountId);

    try {
      if (accountId) {
        sessionStorage.setItem(STORAGE_KEY, accountId);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Failed to persist to sessionStorage:', error);
    }
  };

  // Clear account selection (show all)
  const clearAccount = () => {
    setSelectedAccount(null);
  };

  const value = {
    selectedAccount,
    setSelectedAccount,
    clearAccount
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};

// Custom hook for using account context
export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within AccountProvider');
  }
  return context;
};

export default AccountContext;
